// ---- Interest-management debug HUD ------------------------------------------
// A toggleable (F3) diagnostics layer for the AoI system: a monospace stats
// panel (what we know / what's culled / wire throughput, plus the server's
// world-level view) and a minimap overlay drawing the spatial grid, the
// player's interest rings, and per-cell occupancy.
//
// While the panel is on, net.ts asks the server for its ~2Hz `imstats` feed
// ({t:"imstats",on:true}); the feed carries the server's EFFECTIVE config
// (env-tuned values), so the overlay's grid/rings always match reality. Before
// the first payload arrives we fall back to IM_DEFAULTS for the drawing.
//
// Purely diagnostic — no gameplay effect, nothing here runs while hidden.

import { netStats, sendImstats } from "./net.ts";
import { npcs } from "./npc/index.ts";
import { worldItemMeshes } from "./items/world.ts";
import { setMinimapOverlay } from "./minimap.ts";
import { renderInfo } from "./render.ts";
import { IM_DEFAULTS } from "./shared.ts";

let panel: HTMLDivElement;
let on = false;
let lastRender = 0;
let px = 0, pz = 0; // player position, for centering the rings

export function initImDebug(): void {
  panel = document.createElement("div");
  panel.id = "imdebug";
  panel.style.cssText =
    "position:fixed; top:14px; left:12px; z-index:7; display:none;" +
    "font:11px/1.5 ui-monospace, Menlo, monospace; color:#cfe3cf;" +
    "background:rgba(12,16,12,.82); border:1px solid #3a4a3a; border-radius:8px;" +
    "padding:8px 10px; pointer-events:none; white-space:pre; min-width:230px;";
  document.body.appendChild(panel);
}

export function toggleImDebug(): void {
  on = !on;
  panel.style.display = on ? "block" : "none";
  sendImstats(on); // server starts/stops the ~2Hz imstats feed
  setMinimapOverlay(on ? drawOverlay : null);
}

/** Per-frame from the game loop (next to updateMinimap). Cheap when hidden;
 *  DOM writes throttled to ~4Hz when shown. */
export function updateImDebug(playerX: number, playerZ: number): void {
  if (!on) return;
  px = playerX; pz = playerZ;
  const now = performance.now();
  if (now - lastRender < 250) return;
  lastRender = now;

  const s = netStats();
  const im = s.im;
  const totalNpcs = npcs().length;
  let shownNpcs = 0;
  for (const L of npcs()) if (!L.netHidden && !L.defeated) shownNpcs++;
  const items = worldItemMeshes().length;

  let text = "";

  // ---- Render perf: the first thing to read when diagnosing "smooth for me,
  // choppy for them". FPS + worst-frame separate a steady-but-low GPU (drop the
  // quality preset) from a hitchy one (GC/network). The GPU string tells you
  // instantly whether a player is stuck on an integrated card. ----
  const r = renderInfo();
  if (r) {
    const gpu = r.gpu.length > 40 ? r.gpu.slice(0, 39) + "…" : r.gpu;
    text += "◤ perf\n";
    text += `fps       ${r.fps.toFixed(0)}  (${r.frameMs.toFixed(1)}ms · worst ${r.worstMs.toFixed(0)}ms)\n`;
    text += `draw      ${r.calls} calls · ${(r.triangles / 1000).toFixed(0)}k tris\n`;
    text += `mem       ${r.geometries} geo · ${r.textures} tex · ${r.programs} prog\n`;
    text += `quality   ${r.quality} · dpr ${r.pixelRatio.toFixed(1)}\n`;
    text += `gpu       ${gpu}\n`;
  }

  text += "◤ interest management (F3)\n";
  text += `pos       ${playerX.toFixed(0)}, ${playerZ.toFixed(0)}\n`;
  text += "— client mirror —\n";
  text += `remotes   ${s.others}\n`;
  text += `npcs      ${shownNpcs} shown / ${totalNpcs} built\n`;
  text += `items     ${items}\n`;
  text += `state msg ${s.stateBytes} B   ${(s.stateBytesPerSec / 1024).toFixed(1)} KB/s\n`;
  if (im) {
    const known = im.you.near + im.you.mid + im.you.far;
    const worldEnts = im.world.players - 1 + im.world.npcs; // others + npcs
    text += "— server (you) —\n";
    text += `rings     near ${im.you.near} · mid ${im.you.mid} · far ${im.you.far}\n`;
    text += `known     ${known + im.you.items} (${im.you.items} items)   culled ${Math.max(0, worldEnts - known)}\n`;
    text += `egress    ${im.you.entPerSec} ent/s   ${(im.you.bytesPerSec / 1024).toFixed(1)} KB/s\n`;
    text += "— server (world) —\n";
    text += `conns ${im.conns}   players ${im.world.players}   npcs ${im.world.npcs}   items ${im.world.items}\n`;
    text += `cells     ${im.world.cellsOccupied} occupied (max ${im.world.maxCellOccupancy}/cell)\n`;
    text += `cfg       cell ${im.cfg.cell} · rings ${im.cfg.near}/${im.cfg.mid}/${im.cfg.far} · exit ${im.cfg.exit}`;
  } else {
    text += "— server —\nawaiting imstats…";
  }
  panel.textContent = text;
}

/** Minimap overlay: grid lines every cell, occupancy dots, and the player's
 *  interest rings (near / mid / far solid, exit dashed). */
function drawOverlay(
  ctx: CanvasRenderingContext2D,
  toMap: (x: number, z: number) => [number, number],
  res: number,
  worldSize: number,
): void {
  const im = netStats().im;
  const cfg = im?.cfg ?? {
    cell: IM_DEFAULTS.cellSize,
    near: IM_DEFAULTS.nearRadius, mid: IM_DEFAULTS.midRadius, far: IM_DEFAULTS.farRadius,
    exit: IM_DEFAULTS.farRadius + IM_DEFAULTS.hysteresis,
  };
  const half = worldSize / 2;
  const scale = res / worldSize; // world units → map px

  // Grid.
  ctx.save();
  ctx.strokeStyle = "rgba(255,255,255,0.14)";
  ctx.lineWidth = 1;
  ctx.beginPath();
  for (let w = -half; w <= half; w += cfg.cell) {
    const [gx] = toMap(w, 0);
    ctx.moveTo(gx, 0); ctx.lineTo(gx, res);
    const [, gy] = toMap(0, w);
    ctx.moveTo(0, gy); ctx.lineTo(res, gy);
  }
  ctx.stroke();

  // Cell occupancy (players + NPCs, from the server feed).
  if (im) {
    for (const [cx, cz, n] of im.cells) {
      const wx = -half + (cx + 0.5) * cfg.cell;
      const wz = -half + (cz + 0.5) * cfg.cell;
      const [mx, my] = toMap(wx, wz);
      ctx.fillStyle = "rgba(120,220,120,0.75)";
      ctx.font = "9px ui-monospace, monospace";
      ctx.textAlign = "center";
      ctx.fillText(String(n), mx, my + 3);
    }
  }

  // Interest rings, centered on the player.
  const [cxm, czm] = toMap(px, pz);
  const ring = (r: number, style: string, dash: number[]) => {
    ctx.strokeStyle = style;
    ctx.setLineDash(dash);
    ctx.lineWidth = 1.2;
    ctx.beginPath();
    ctx.arc(cxm, czm, r * scale, 0, Math.PI * 2);
    ctx.stroke();
  };
  ring(cfg.near, "rgba(120,255,120,0.8)", []);
  ring(cfg.mid, "rgba(255,220,199,0.7)", []);
  ring(cfg.far, "rgba(255,150,90,0.7)", []);
  ring(cfg.exit, "rgba(255,90,90,0.7)", [4, 4]);
  ctx.restore();
}
